import React, { useCallback, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  firstError,
  hasError,
  validateCollection,
  validateCollectionField,
} from "../collectionValidation";
import { MediaContext } from "../../common/media-context";
import { useUploadCollectionThumbnail } from "../collectionServices";
import { useSnackAlert } from "../../../common/use-snackAlert";
import { useIsAdmin, useIsCreatorManager } from "../../../auth/authUtils";
import { anyTrue } from "../../../util/ext";
import { useBiblioboardUrl } from "../../common/BiblioboardLink";

import Grid from "@material-ui/core/Grid";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";

import EditColumn from "../../common/EditColumn";
import EditThumbnailOverlay from "../../common/EditThumbnailOverlay";
import DefaultContentSort from "./DefaultContentSort";
import CopyIcon from "../../common/CopyIcon";
import ContentGroupOwner from "../../common/ContentGroupOwner";

const useStyles = makeStyles(theme => ({
  root: {
    padding: 0,
  },

  charCount: {
    marginTop: 5,
    width: "100%",
    textAlign: "right",
  },

  column: {
    padding: "0 50px 0 50px",
  },

  rte: {
    borderWidth: 1,
  },
}));

const validator = {
  validateField: validateCollectionField,
  validate: validateCollection,
};

const EditCollectionInfoColumn = props => {
  const classes = useStyles();
  const context = useContext(MediaContext);
  const isAdmin = useIsAdmin();
  const isCreatorManager = useIsCreatorManager();
  const [url, showUrl] = useBiblioboardUrl(context.media);

  const thumbnailUpload = useUploadCollectionThumbnail();
  const showAlert = useSnackAlert();

  const formBuilder = useCallback(
    handlers => {
      return (
        <Grid spacing={4} container>
          <Grid item>
            <EditThumbnailOverlay
              accept="jpg, jpeg, image/jpeg, image/jpg, png, image/png"
              showAlert={showAlert}
              uploader={thumbnailUpload}
              hasThumbnailLock
            />
          </Grid>

          <Grid
            className={classes.column}
            container
            item
            direction="column"
            xs
            spacing={4}
          >
            <Grid item>
              <TextField
                id="collection-name"
                fullWidth
                label="Title"
                value={context.media.name}
                onChange={handlers.onTextChange(["name"])}
                error={hasError(context.errors, "name")}
                helperText={firstError(context.errors, "name")}
                onBlur={() => handlers.onBlur("name", context.media.name)}
              />
            </Grid>

            <Grid item>
              <TextField
                id="collection-sub-name"
                fullWidth
                label="Subtitle"
                value={context.media.subName}
                onChange={handlers.onTextChange(["subName"])}
                error={hasError(context.errors, "subName")}
                helperText={firstError(context.errors, "subName")}
                onBlur={() => handlers.onBlur("subName", context.media.subName)}
              />
            </Grid>

            <Grid item>
              <TextField
                id="collection-id"
                disabled
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <CopyIcon value={context.media.id} text="Collection ID" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
                label="Collection ID"
                value={context.media.id}
              />
            </Grid>

            <Grid item>
              <ContentGroupOwner />
            </Grid>

            {anyTrue([isAdmin, isCreatorManager]) && (
              <Grid item>
                <TextField
                  id="collection-internal-code"
                  fullWidth
                  maxLength={255}
                  label="Identifier (internal code)"
                  value={context.media.internalCode || ""}
                  onChange={handlers.onTextChange(["internalCode"])}
                  error={hasError(context.errors, "internalCode")}
                  helperText={firstError(context.errors, "internalCode")}
                  onBlur={() =>
                    handlers.onBlur("internalCode", context.media.internalCode)
                  }
                />
                <div className={classes.charCount}>
                  {context.media.internalCode?.length || 0}/255
                </div>
              </Grid>
            )}

            {showUrl && (
              <Grid item>
                <TextField
                  id="biblioboard-url-collection"
                  fullWidth
                  disabled
                  label="BiblioBoard URL"
                  value={url}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <CopyIcon value={url} text="BiblioBoard URL" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            )}
          </Grid>

          <Grid item xs={4}>
            <DefaultContentSort />
          </Grid>
        </Grid>
      );
    },
    [
      classes.charCount,
      classes.column,
      context.errors,
      context.media.internalCode,
      context.media.name,
      context.media.subName,
      context.media.id,
      isAdmin,
      isCreatorManager,
      showAlert,
      showUrl,
      thumbnailUpload,
      url,
    ],
  );

  return (
    <EditColumn
      className={classes.root}
      validator={validator}
      formBuilder={formBuilder}
    />
  );
};

export default EditCollectionInfoColumn;
